import stableStringify from 'fast-json-stable-stringify';
import type { CrossObjectFilter, Filter, FilterGroup } from '../../gql-types';
import { recordSortDuration, SortContext } from './sortPerfBuffer';

function normalizeFilter(filter: Filter): Filter {
  return filter.values != null
    ? { ...filter, values: [...filter.values].sort() }
    : filter;
}

function normalizeCrossObjectFilters(
  crossObjectFilters: NonNullable<FilterGroup['crossObjectFilters']>
): CrossObjectFilter[] {
  const normalized = crossObjectFilters.map((cof) =>
    cof.filterGroups != null
      ? { ...cof, filterGroups: _sortFilterGroupsImpl(cof.filterGroups) }
      : cof
  );
  return normalized.sort((a, b) => {
    const keyA = stableStringify(a);
    const keyB = stableStringify(b);
    return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
  });
}

function _sortFilterGroupsImpl(
  filterGroups: Array<FilterGroup>
): Array<FilterGroup> {
  const withKeys = filterGroups.map((group) => {
    const filtersWithKeys = group.filters.map((filter) => {
      const normalized = normalizeFilter(filter);
      return { filter: normalized, key: stableStringify(normalized) };
    });
    filtersWithKeys.sort((a, b) =>
      a.key < b.key ? -1 : a.key > b.key ? 1 : 0
    );
    const normalizedGroup: FilterGroup =
      group.crossObjectFilters != null
        ? {
            ...group,
            filters: filtersWithKeys.map(({ filter }) => filter),
            crossObjectFilters: normalizeCrossObjectFilters(
              group.crossObjectFilters
            ),
          }
        : { ...group, filters: filtersWithKeys.map(({ filter }) => filter) };
    return {
      group: normalizedGroup,
      groupKey: filtersWithKeys.map(({ key }) => key).join('\x02'),
    };
  });

  withKeys.sort((a, b) =>
    a.groupKey < b.groupKey ? -1 : a.groupKey > b.groupKey ? 1 : 0
  );
  return withKeys.map(({ group }) => group);
}

export function sortFilterGroups(
  filterGroups: Array<FilterGroup>,
  { context }: { context: SortContext } = { context: 'standard' }
): Array<FilterGroup> {
  const start = performance.now();
  const result = _sortFilterGroupsImpl(filterGroups);
  recordSortDuration({ durationMs: performance.now() - start, context });
  return result;
}

export const normalizeVariables = <
  TVariables extends { filterGroups: FilterGroup[] },
>(
  variables: TVariables,
  context: SortContext
): TVariables => ({
  ...variables,
  filterGroups: sortFilterGroups(variables.filterGroups, { context }),
});
